import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import type { EntityPickerOptions } from '@adornis/entity-picker/client/entity-picker-options.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BHPromptWorkshopAdd } from './bh-prompt-workshop-add.js';

@customElement('bh-prompt-workshop-select-or-add')
export class BHPromptWorkshopSelectOrAdd extends TrDialog<Maybe<Workshop>> {
  @property({ attribute: false }) institutionID = new RXController<string | null>(this, null);
  @property({ attribute: false }) selectedProductID?: string;

  static selectOrAdd(institutionID: string): Promise<Maybe<Workshop>> {
    return this.showPopup<Maybe<Workshop>>({
      props: { institutionID },
    });
  }

  override async cancel(): Promise<void> {
    await this.close(null);
  }

  static override get element_name() {
    return 'bh-prompt-workshop-select-or-add';
  }

  override connectedCallback() {
    super.connectedCallback();
  }

  private async handleAddWorkshop() {
    if (!this.institutionID.value) return;

    const newWorkshop = await BHPromptWorkshopAdd.add(this.institutionID.value);

    if (newWorkshop) {
      await this.close(newWorkshop);
    }
  }

  override render() {
    if (this.institutionID.isLoading || !this.institutionID.value) {
      return html`<x-loader></x-loader>`;
    }

    return html`
      <x-flex space="lg">
        <x-flex horizontal crossaxis-center space-between>
          <x-h2>${this._translation.translate('workshop', 'add')}</x-h2>
          <tr-button-small icon="add" @click=${() => this.handleAddWorkshop()}>
            ${this._translation.translate('workshop', 'add')}
          </tr-button-small>
        </x-flex>

        <x-entity-picker
          placeholder="Workshop auswählen..."
          .options=${{
            class: Workshop,
            filter: new AdornisFilter({
              searchFields: ['name'],
              aspects: [
                new StringAspect({ fieldName: 'institutionID', mode: 'equals', value: this.institutionID.value }),
              ],
            }),
            limit: 200,
            selectionSet: { name: 1, timeframe: { name: 1 }, appointments: { start: 1 } },
          } satisfies EntityPickerOptions<typeof Workshop>}
          .renderString=${(workshop: Maybe<Workshop>) => workshop?.name}
          .renderItem=${workshop => {
            return renderEntityPickerItem(
              workshop?.name,
              workshop?.timeframe?.name,
              workshop?.appointments?.start?.toFormat('dd.LL.yyyy'),
            );
          }}
          .value=${this.selectedProductID}
          @value-picked=${(e: ValueEvent<string>) => (this.selectedProductID = e.detail.value)}
        ></x-entity-picker>

        <tr-button-small
          ?disabled=${!this.selectedProductID}
          @click=${async () => {
            const workshopID = this.selectedProductID;
            if (!workshopID) return;
            const workshopReq = getByID(Workshop, workshopID)(selectionSet(() => Workshop, 1));
            const workshop = await XDialog.waitFor(workshopReq);
            if (!workshop) throw new Error('could not fetch workshop');
            this.close(workshop as Workshop);
          }}
        >
          ${this._translation.translate('SAVE')}
        </tr-button-small>
      </x-flex>
    `;
  }

  override getInnerWrapper(options: Required<DialogOptions>) {
    const wrapperElem = super.getInnerWrapper(options);
    wrapperElem.style.setProperty('background', this.colors.white);
    wrapperElem.style.setProperty('border-radius', this.sizes.borderRadius);
    wrapperElem.style.setProperty('width', `min(90%, 800px)`);
    wrapperElem.style.setProperty('height', `min(90vh, 600px)`);
    wrapperElem.style.setProperty('padding', this.spacing.lg);
    wrapperElem.style.setProperty('overflow-y', 'auto');
    return wrapperElem;
  }
}

function renderEntityPickerItem(name: string, timeframeName: Maybe<string>, startDate: Maybe<string>) {
  return html`
    <x-flex
      ${css({
        padding: `10px 20px`,
        cursor: 'pointer',
      })}
      space="md"
    >
      <x-flex horizontal ${css({ gap: '10px' })}>
        <x-text> ${name} </x-text>
        <x-text ${css({ opacity: 0.6 })}><i> ${timeframeName} </i></x-text>
        <x-text ${css({ opacity: 0.6 })}><i> ${startDate} </i></x-text>
      </x-flex>
    </x-flex>
  `;
}
